import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Order } from "../../types";
import { ApiService, api } from "../../services/api";
import {
  PollingService,
  PollingConfig,
  PollingCallbacks,
} from "../../services/polling";
import { FRONTEND_CONFIG, getPollingIntervalMs } from "../../config";
import LoadingSpinner from "../../components/LoadingSpinner";
import ErrorMessage from "../../components/ErrorMessage";
import Pagination from "../../components/Pagination";
import PageHeader from "../../components/PageHeader";
import { formatTimestampFull } from "../../utils/dateFormatter";
import { useAppSelector } from "../../store/hooks";
import { API_BASE_URL } from "../../utils/apiConfig";
import { useLanguage } from "../../contexts/LanguageContext";
import {
  ShoppingCart,
  Package,
  Truck,
  CheckCircle,
  X,
  Clock,
  Settings,
  AlertCircle,
  RotateCcw,
  Search,
  Filter,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Database,
  Wifi,
  WifiOff,
  RefreshCw,
  Activity,
  BarChart3,
  Users,
  Globe,
  Phone,
  MapPin,
  Calendar,
  DollarSign,
  Eye,
  Edit,
  UserCheck,
  Plus,
  XCircle,
  Download,
  Upload,
  Mail,
  User,
  Building,
  Flag,
  LayoutGrid,
  List,
} from "lucide-react";
import {
  ShippingStats,
  EnhancedStats,
  Shipment,
  AnalyticsData,
  StatusInfo,
  MongoDBStatus,
  SortByType,
  SortOrderType,
  getCountryFlag,
  countryFlags,
} from "./types";
import AddOrderModal from "./AddOrderModal";
import BulkImportModal from "./BulkImportModal";

interface OrdersMainProps {
  currentUser?: {
    id: string;
    username: string;
    email: string;
    role: string;
    countries: string[];
    permissions: string[];
    profile: {
      firstName: string;
      lastName: string;
      phone: string;
      department: string;
    };
  };
}

interface CacheInfo {
  lastUpdate: string | null;
  totalOrders: number;
  refreshInterval: number;
  isLoading: boolean;
}

const OrdersMain: React.FC<OrdersMainProps> = ({ currentUser }) => {
  const { t } = useLanguage();
  // State management
  const [orders, setOrders] = useState<Order[]>([]);
  const [stats, setStats] = useState<EnhancedStats | null>(null);
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [shippingStats, setShippingStats] = useState<ShippingStats | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mongoDBStatus, setMongoDBStatus] = useState<MongoDBStatus>({
    connected: false,
    lastUpdate: "",
  });
  const [cacheInfo, setCacheInfo] = useState<CacheInfo | null>(null);

  // AlterCPA state removed for Serbia-CC
  // const altercpaState = useAppSelector((state) => state.altercpa);

  // Filter and search state
  const [searchTerm, setSearchTerm] = useState(""); // Active search term (triggers API call)
  const [searchInput, setSearchInput] = useState(""); // Input field value (local state)
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  
  // Auto-filter by country for country-post-manager and call-operator roles
  const getInitialCountryFilter = () => {
    if (
      (currentUser?.role === "country-post-manager" || currentUser?.role === "call-operator") &&
      currentUser.countries && 
      currentUser.countries.length > 0
    ) {
      // Use the first country from user's countries array
      return currentUser.countries[0].toLowerCase();
    }
    return "all";
  };
  
  const [countryFilter, setCountryFilter] = useState<string>(getInitialCountryFilter());
  const [isCountryRestricted, setIsCountryRestricted] = useState(
    currentUser?.role === "country-post-manager" || currentUser?.role === "call-operator"
  );
  const [sortBy, setSortBy] = useState<SortByType>("time");
  const [sortOrder, setSortOrder] = useState<SortOrderType>("desc");
  const [timePeriod, setTimePeriod] = useState<string>("this-month");

  // Custom date range state
  const [showCustomDateRange, setShowCustomDateRange] = useState(false);
  const [customDateRange, setCustomDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    end: new Date().toISOString().split("T")[0],
  });

  // Applied custom date range (only updates when user clicks "Apply Range")
  const [appliedCustomDateRange, setAppliedCustomDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    end: new Date().toISOString().split("T")[0],
  });

  // Refresh trigger - increments when user clicks Refresh button
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(100);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);

  // Export state
  const [isExporting, setIsExporting] = useState(false);

  // Add order modal state
  const [isAddOrderModalOpen, setIsAddOrderModalOpen] = useState(false);
  
  // Bulk import modal state
  const [isBulkImportModalOpen, setIsBulkImportModalOpen] = useState(false);

  // View mode state (table or card view)
  const [viewMode, setViewMode] = useState<"table" | "cards">("table");

  // Dynamic width calculation for responsive table
  const [maxWidth, setMaxWidth] = useState<string>("100%");

  useEffect(() => {
    const handleResize = () => {
      const windowWidth = window.innerWidth;
      
      // Calculate padding based on breakpoints (matching parent: px-4 sm:px-6 lg:px-8)
      // px-4 = 16px (1rem), sm:px-6 = 24px (1.5rem), lg:px-8 = 32px (2rem)
      let pagePadding = 20; // default mobile padding (px-4)
      if (windowWidth >= 640) { // sm breakpoint
        pagePadding = 28; // sm:px-6
      }
      if (windowWidth >= 1024) { // lg breakpoint
        pagePadding = 36; // lg:px-8
      }
      
      // Sidebar only exists on lg+ screens (1024px+) and is 256px wide (w-64)
      const sideMenuWidth = windowWidth >= 1024 ? 256 : 0;
      
      // Total horizontal padding (left + right)
      const totalPadding = pagePadding * 2;
      
      const newWidth = windowWidth - sideMenuWidth - totalPadding;
      setMaxWidth(`${newWidth}px`);
    };

    handleResize(); // initial
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Status mapping for display with proper icons
  const statusMap: {
    [key: number]: {
      name: string;
      color: string;
      icon: React.ReactNode;
      category: string;
    };
  } = {
    1: {
      name: t('orders.statusNew'),
      color: "bg-blue-100 text-blue-800",
      icon: <ShoppingCart className="w-4 h-4" />,
      category: "processing",
    },
    2: {
      name: t('orders.statusProcessing'),
      color: "bg-yellow-100 text-yellow-800",
      icon: <Settings className="w-4 h-4" />,
      category: "processing",
    },
    3: {
      name: t('orders.statusCallback'),
      color: "bg-orange-100 text-orange-800",
      icon: <Phone className="w-4 h-4" />,
      category: "processing",
    },
    4: {
      name: t('orders.statusApproved'),
      color: "bg-green-100 text-green-800",
      icon: <CheckCircle className="w-4 h-4" />,
      category: "processing",
    },
    5: {
      name: t('orders.statusCancelled'),
      color: "bg-red-100 text-red-800",
      icon: <XCircle className="w-4 h-4" />,
      category: "cancelled",
    },
    6: {
      name: t('orders.statusPackaging'),
      color: "bg-purple-100 text-purple-800",
      icon: <Package className="w-4 h-4" />,
      category: "shipping",
    },
    7: {
      name: t('orders.statusSending'),
      color: "bg-indigo-100 text-indigo-800",
      icon: <Upload className="w-4 h-4" />,
      category: "shipping",
    },
    8: {
      name: t('orders.statusTransfer'),
      color: "bg-cyan-100 text-cyan-800",
      icon: <Truck className="w-4 h-4" />,
      category: "shipping",
    },
    9: {
      name: t('orders.statusDelivered'),
      color: "bg-emerald-100 text-emerald-800",
      icon: <CheckCircle className="w-4 h-4" />,
      category: "completed",
    },
    10: {
      name: t('orders.statusCompleted'),
      color: "bg-green-100 text-green-800",
      icon: <CheckCircle className="w-4 h-4" />,
      category: "completed",
    },
    11: {
      name: t('orders.statusReturn'),
      color: "bg-red-100 text-red-800",
      icon: <RotateCcw className="w-4 h-4" />,
      category: "cancelled",
    },
  };

  // Fetch data functions - optimized to reduce re-renders
  const fetchOrdersAndStats = useCallback(
    async (page: number) => {
      try {
        // Build query parameters for server-side pagination
        const params: any = {
          page,
          limit: itemsPerPage,
          sortBy,
          sortOrder,
          search: searchTerm.trim() || undefined,
          status: statusFilter !== "all" ? parseInt(statusFilter) : undefined,
          country:
            countryFilter !== "all" ? countryFilter.toUpperCase() : undefined,
          period: timePeriod,
        };

        // Add custom date range if selected (use applied values, not the input values)
        if (timePeriod === "custom") {
          params.customStartDate = appliedCustomDateRange.start;
          params.customEndDate = appliedCustomDateRange.end;
        }

        const response = await ApiService.getPaginatedOrders(params);

        if (response.success) {
          setOrders(response.data.orders);
          setTotalPages(response.data.pagination?.totalPages || 1);
          setTotalOrders(response.data.pagination?.total || 0);

          // Use the enhanced stats calculated by backend
          const statsData = response.data.stats || {};
          const transformedStats: EnhancedStats = {
            ...statsData,
            byCountry: (statsData as any).byCountry || {},
            byCategory: (statsData as any).byCategory || {},
            packagingOrders: (statsData as any).packagingOrders || 0,
            shippingOrders: (statsData as any).shippingOrders || 0,
          };
          setStats(transformedStats);
          setMongoDBStatus({
            connected: true,
            lastUpdate: statsData.lastUpdate || new Date().toISOString(),
          });
          setError(null);
        } else {
          setError("Failed to fetch data");
        }
      } catch (err) {
        console.error("Error fetching orders:", err);
        setError("Failed to fetch data");
        setMongoDBStatus({ connected: false, lastUpdate: "" });
      }
    },
    [
      itemsPerPage,
      sortBy,
      sortOrder,
      searchTerm,
      statusFilter,
      countryFilter,
      timePeriod,
      appliedCustomDateRange.start,
      appliedCustomDateRange.end,
    ]
  );

  const fetchShipments = useCallback(async () => {
    try {
      const response = await ApiService.getShipments();
      if (response.success) {
        setShipments(response.data.shipments);
      }
    } catch (err) {
      console.error("Error fetching shipments:", err);
    }
  }, []);

  const fetchShippingStats = useCallback(async () => {
    try {
      const response = await ApiService.getShippingStats();
      if (response.success) {
        setShippingStats(response.data as any);
      }
    } catch (err) {
      console.error("Error fetching shipping stats:", err);
    }
  }, []);

  const fetchCacheInfo = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/enhanced-orders/cache/info`);
      const data = await response.json();
      
      if (data.success) {
        setCacheInfo(data.data);
      } else {
        // Cache is disabled or not available
        setCacheInfo(null);
      }
    } catch (err) {
      // Cache is disabled or not available
      setCacheInfo(null);
      console.log("Cache service not available (disabled)");
    }
  }, []);

  const refreshCache = useCallback(async () => {
    try {
      setCacheInfo((prev) => prev ? { ...prev, isLoading: true } : null);
      
      const response = await fetch(`${API_BASE_URL}/api/enhanced-orders/cache/refresh`, {
        method: "POST",
      });
      const data = await response.json();
      
      if (data.success) {
        await fetchCacheInfo();
        // Trigger a data refresh to show updated data
        setRefreshTrigger((prev) => prev + 1);
      }
    } catch (err) {
      console.error("Error refreshing cache:", err);
    }
  }, [fetchCacheInfo]);

  // Client-side filtering removed - now using server-side pagination and filtering

  // Effects - Load data when filters or page changes
  // timePeriod triggers immediate reload for preset options (today, this-week, etc.)
  // appliedCustomDateRange triggers reload only when "Apply Range" is clicked
  // refreshTrigger is for manual refresh button
  // searchTerm triggers reload via debounced search (500ms delay) or manual search
  useEffect(() => {
    // Use requestIdleCallback to defer data loading until the browser is idle
    // This prevents blocking the main thread and eliminates performance violations
    const loadData = async () => {
      setLoading(true);
      try {
        await fetchOrdersAndStats(currentPage);
      } finally {
        setLoading(false);
      }
    };

    // Check if requestIdleCallback is available (modern browsers)
    if (window.requestIdleCallback) {
      window.requestIdleCallback(
        () => {
          loadData();
        },
        { timeout: 1000 }
      ); // Fallback timeout of 1 second
    } else {
      // Fallback for older browsers - use setTimeout with 0 delay
      setTimeout(() => {
        loadData();
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    sortBy,
    sortOrder,
    searchTerm || "", // Ensure searchTerm is always a string to prevent dependency array size changes
    statusFilter,
    countryFilter,
    timePeriod,
    appliedCustomDateRange.start,
    appliedCustomDateRange.end,
    refreshTrigger,
  ]);

  // Load shipments, shipping stats, and cache info only once on mount
  useEffect(() => {
    fetchShipments();
    fetchShippingStats();
    fetchCacheInfo();
    
    // Poll cache info every minute
    const interval = setInterval(() => {
      fetchCacheInfo();
    }, 60000);
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRetry = () => {
    setError(null);
    // Increment refresh trigger to force reload with current timePeriod and customDateRange values
    setRefreshTrigger((prev) => prev + 1);
  };

  // Handle adding a new order
  const handleAddOrder = async (orderData: any) => {
    try {
      console.log("➕ [ADD ORDER] Creating new order:", orderData);
      
      // Get auth token from localStorage
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error("Authentication token not found. Please log in again.");
      }
      
      // Call backend API to create the order
      const response = await fetch(`${API_BASE_URL}/api/orders/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify(orderData),
      });

      const result = await response.json();

      if (response.status === 403) {
        console.error("❌ [ADD ORDER] Permission denied:", result.error);
        alert(`Permission denied: ${result.error}`);
        throw new Error(result.error);
      }

      if (result.success) {
        console.log("✅ [ADD ORDER] Order created successfully:", result.data);
        alert(`Order #${result.data.id} created successfully!\nCountry: ${result.data.country}\nCustomer: ${result.data.customer}`);
        
        // Refresh the orders list
        setRefreshTrigger((prev) => prev + 1);
        
        return result.data;
      } else {
        throw new Error(result.error || "Failed to create order");
      }
    } catch (error: any) {
      console.error("❌ [ADD ORDER] Error creating order:", error);
      alert(`Failed to create order: ${error.message || "Unknown error"}`);
      throw error;
    }
  };

  // Handle bulk import
  const handleBulkImport = async (orders: any[]) => {
    try {
      console.log(`📦 [BULK IMPORT] Importing ${orders.length} orders`);
      
      // Get auth token from localStorage
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error("Authentication token not found. Please log in again.");
      }
      
      // Call backend API to create multiple orders
      const response = await fetch(`${API_BASE_URL}/api/orders/bulk-create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ orders }),
      });

      const result = await response.json();

      if (response.status === 403) {
        console.error("❌ [BULK IMPORT] Permission denied:", result.error);
        alert(`Permission denied: ${result.error}`);
        throw new Error(result.error);
      }

      if (result.success) {
        console.log(`✅ [BULK IMPORT] ${result.data.created} orders created successfully`);
        alert(
          `Bulk import completed!\n` +
          `✅ Created: ${result.data.created}\n` +
          `⚠️ Skipped (duplicates): ${result.data.skipped}\n` +
          `❌ Failed: ${result.data.failed}`
        );
        
        // Refresh the orders list
        setRefreshTrigger((prev) => prev + 1);
        
        return result.data;
      } else {
        throw new Error(result.error || "Failed to import orders");
      }
    } catch (error: any) {
      console.error("❌ [BULK IMPORT] Error importing orders:", error);
      alert(`Failed to import orders: ${error.message || "Unknown error"}`);
      throw error;
    }
  };

  // Export all orders to CSV
  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      console.log("📥 [EXPORT] Starting CSV export...");
      console.log("📊 [EXPORT] Expected total orders:", totalOrders);

      // Build export parameters
      const params: any = {
        sortBy,
        sortOrder,
        search: searchTerm.trim() || undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
        country:
          countryFilter !== "all" ? countryFilter.toUpperCase() : undefined,
        period: timePeriod,
      };

      if (timePeriod === "custom") {
        params.customStartDate = appliedCustomDateRange.start;
        params.customEndDate = appliedCustomDateRange.end;
      }

      console.log("📅 [EXPORT] Period:", timePeriod);
      if (timePeriod === "custom") {
        console.log(
          "📅 [EXPORT] Custom range:",
          params.customStartDate,
          "to",
          params.customEndDate
        );
      }

      // Call dedicated export endpoint using api (exported instance)
      const response = await api.exportOrders(params);

      if (response.success && response.data.orders) {
        const allOrders = response.data.orders;
        console.log("📦 [EXPORT] Fetched", allOrders.length, "orders");
        console.log(
          "📋 [EXPORT] Order IDs:",
          allOrders.map((o: any) => o.id).join(", ")
        );

        // Convert to CSV
        const csvContent = convertOrdersToCSV(allOrders);

        // Create filename with date range
        let filename = "orders";
        if (timePeriod === "custom") {
          filename += `_${params.customStartDate}_to_${params.customEndDate}`;
        } else {
          filename += `_${timePeriod}`;
        }
        filename += `_${new Date().toISOString().split("T")[0]}.csv`;

        // Trigger download
        downloadCSV(csvContent, filename);

        console.log("✅ [EXPORT] CSV exported successfully:", filename);
        console.log("📊 [EXPORT] Total orders exported:", allOrders.length);

        // Show success message
        alert(
          `Successfully exported ${allOrders.length} orders to ${filename}`
        );
      } else {
        console.error("❌ [EXPORT] Failed to fetch orders for export");
        alert("Failed to export orders. Please try again.");
      }
    } catch (error) {
      console.error("❌ [EXPORT] Error exporting CSV:", error);
      alert("Error exporting CSV. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  // Convert orders array to CSV format
  const convertOrdersToCSV = (orders: Order[]): string => {
    const headers = [
      "Order ID",
      "Date",
      "Status",
      "Customer Name",
      "Phone",
      "Email",
      "Country",
      "City",
      "Address",
      "Product",
      "Quantity",
      "Price",
      "Currency",
      "Account ID",
      "Account Name",
      "Source ID",
      "Source Name",
      "Tracking ID",
      "Shipping Company",
      "Call Center Status",
      "Reached Packing",
    ];

    const rows = orders.map((order: any) => {
      const orderDate = order.time
        ? new Date(
            order.time < 10000000000 ? order.time * 1000 : order.time
          ).toISOString()
        : "N/A";

      return [
        order.id,
        orderDate,
        order.statusInfo?.name || `Status ${order.status}`,
        `"${(order.name || "N/A").replace(/"/g, '""')}"`, // Escape quotes
        order.phone || "N/A",
        order.email || "N/A",
        (order.country || "N/A").toUpperCase(),
        `"${(order.city || "N/A").replace(/"/g, '""')}"`,
        `"${(order.addr || "N/A").replace(/"/g, '""')}"`,
        `"${(order.offername || "N/A").replace(/"/g, '""')}"`,
        order.count || 1,
        order.price || 0,
        (order.currency || "N/A").toUpperCase(),
        order.user || "N/A",
        `"${(order.accountInfo?.username || "N/A").replace(/"/g, '""')}"`,
        order.wm || "N/A",
        `"${(order.sourceInfo?.name || "N/A").replace(/"/g, '""')}"`,
        order.trackingId || "N/A",
        order.shippingCompany || "N/A",
        order.callcentarStatus || "N/A",
        order.reachedPacking ? "Yes" : "No",
      ].join(",");
    });

    return [headers.join(","), ...rows].join("\n");
  };

  // Download CSV file
  const downloadCSV = (content: string, filename: string) => {
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  // Handle search execution
  const handleSearch = () => {
    const trimmedSearch = searchInput.trim();
    setSearchTerm(trimmedSearch);
    setCurrentPage(1); // Reset to first page on new search
  };

  // Debounced search for better UX (optional - user can still use manual search)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // Only trigger search if user has stopped typing for 500ms
      const trimmedInput = searchInput.trim();
      if (trimmedInput !== searchTerm) {
        setSearchTerm(trimmedInput);
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchInput, searchTerm]);

  // Handle Enter key press in search input (preventDefault + refocus so input stays focused)
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const formatPrice = (price: number, currency: string) => {
    return `${price.toFixed(2)} ${currency.toUpperCase()}`;
  };

  // Memoize unique countries to avoid recalculating on every render
  const uniqueCountries = useMemo(() => {
    if (!orders || orders.length === 0) return [];
    const countries = Array.from(new Set(orders.map((order) => order.country)));
    return countries.sort();
  }, [orders]);

  // Pagination logic - now using server-side pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchTerm,
    statusFilter,
    countryFilter,
    sortBy,
    sortOrder,
    timePeriod,
    appliedCustomDateRange.start,
    appliedCustomDateRange.end,
  ]);

  const renderStats = () => {
    if (!stats) return null;

    return (
      <div className="bg-white border-b border-gray-200">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ShoppingCart className="w-8 h-8 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-blue-600">
                    Total Orders
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.total || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-purple-50 rounded-lg p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Package className="w-8 h-8 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-purple-600">
                    Packaging
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.byStatus?.[6] || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-orange-50 rounded-lg p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Phone className="w-8 h-8 text-orange-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-orange-600">
                    Callback
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.byStatus?.[3] || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-red-50 rounded-lg p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <XCircle className="w-8 h-8 text-red-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-red-600">Cancelled</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.byStatus?.[5] || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-green-600">
                    Highest Order ID
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    #{stats.highestId || "N/A"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderFilters = () => (
    <div className="bg-gray-50 border-b border-gray-200">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-4" >
        <div className="flex flex-col gap-4">
          {/* Search Row - Full Width */}
          <div className="w-full">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder={t('orders.searchPlaceholder')}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={handleSearchKeyPress}
                  className="pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-[#264a3e] focus:border-[#264a3e] w-full"
                />
                {searchInput && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 touch-manipulation"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleSearch}
                className="inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-[#264a3e] hover:bg-[#1e3a32] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] transition-colors touch-manipulation flex-shrink-0"
                title={t('common.search')}
              >
                <Search className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">{t('common.search')}</span>
              </button>
              {/* Mobile View Toggle */}
              <button
                onClick={() => setViewMode(viewMode === "table" ? "cards" : "table")}
                className="lg:hidden inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] transition-colors touch-manipulation flex-shrink-0"
                title={viewMode === "table" ? t('orders.switchToCardView') : t('orders.switchToTableView')}
              >
                {viewMode === "table" ? (
                  <LayoutGrid className="w-4 h-4" />
                ) : (
                  <List className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          {/* Filters Row - Full Width */}
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 w-full">
            {/* Country Filter */}
            <div className="w-full sm:w-auto flex-1 sm:flex-initial">
              {isCountryRestricted ? (
                <div className="flex items-center space-x-2 rounded-lg w-full sm:w-auto">
                  <MapPin className="w-4 h-4 sm:mr-2" />
                  <span className="text-sm font-medium text-gray-900">
                    {countryFlags[countryFilter] || ""} {countryFilter.toUpperCase()}
                  </span>
                  <span className="text-xs text-blue-600 hidden sm:inline">(Restricted)</span>
                </div>
              ) : (
                <div className="flex  items-center space-x-2 rounded-lg w-full sm:w-auto">
                  <MapPin className="w-4 h-4 sm:mr-2 text-gray-500"  />
                  <select
                    value={countryFilter}
                    onChange={(e) => setCountryFilter(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-[#264a3e] focus:border-[#264a3e] text-sm flex-1"
                  >
                    <option value="all">{t('callCenter.allCountries')}</option>
                    {uniqueCountries.map((country) => (
                      <option key={country} value={country}>
                        {countryFlags[country] || ""} {country.toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Status Filter */}
            <div className="w-full sm:w-auto flex-1 sm:flex-initial flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-[#264a3e] focus:border-[#264a3e] text-sm flex-1"
              >
                <option value="all">{t('common.all')} {t('common.status')}</option>
                <option value="packaging">{t('orders.statusPackaging')}</option>
                <option value="shipping">{t('orders.statusSending')}</option>
                <option value="completed">{t('orders.statusCompleted')}</option>
                <option value="cancelled">{t('orders.statusCancelled')}</option>
              </select>
            </div>

            {/* Sort */}
            <div className="w-full sm:w-auto flex-1 sm:flex-initial flex items-center space-x-2">
              <ArrowUpDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortByType)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-[#264a3e] focus:border-[#264a3e] text-sm flex-1"
              >
                <option value="time">{t('common.sortBy')} {t('common.date')}</option>
                <option value="id">{t('common.sortBy')} {t('orders.orderId')}</option>
                <option value="name">{t('common.sortBy')} {t('common.name')}</option>
                <option value="status">{t('common.sortBy')} {t('common.status')}</option>
                <option value="country">{t('common.sortBy')} {t('common.country')}</option>
                <option value="price">{t('common.sortBy')} {t('common.price')}</option>
              </select>
              <button
                onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors touch-manipulation flex-shrink-0"
                title={sortOrder === "asc" ? t('orders.sortDescending') : t('orders.sortAscending')}
              >
                {sortOrder === "asc" ? (
                  <ArrowUp className="w-4 h-4" />
                ) : (
                  <ArrowDown className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderOrdersCards = () => {
    const getStatusInfo = (status: number) => {
      return statusMap[status] || {
        name: `Status ${status}`,
        color: "bg-gray-100 text-gray-800",
        icon: <AlertCircle className="w-3 h-3" />,
      };
    };

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {(orders || []).map((order: Order) => {
          const statusInfo = getStatusInfo(order.status);
          return (
            <div key={order.id} className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow p-4">
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="text-lg font-bold text-gray-900">#{order.id}</div>
                  <div className="text-xs text-gray-500 mt-1">{formatTimestampFull(order.time)}</div>
                </div>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}
                >
                  {statusInfo.icon}
                  <span className="ml-1">{statusInfo.name}</span>
                </span>
              </div>

          {/* Customer Info */}
          <div className="mb-3">
            <div className="flex items-center mb-1">
              <User className="w-4 h-4 text-gray-400 mr-2" />
              <div className="text-sm font-medium text-gray-900 truncate">{order.name || "N/A"}</div>
            </div>
            <div className="flex items-center text-xs text-gray-500">
              <Phone className="w-3 h-3 mr-1" />
                      {order.phone || t('orders.notAvailable')}
            </div>
          </div>

          {/* Location */}
          <div className="mb-3 flex items-center">
            <MapPin className="w-4 h-4 text-gray-400 mr-2" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center">
                <span className="mr-1">{getCountryFlag(order.country || "")}</span>
                <span className="text-sm font-medium text-gray-900">{(order.country || "").toUpperCase()}</span>
              </div>
              <div className="text-xs text-gray-500 truncate">{order.city || "N/A"}</div>
            </div>
          </div>

          {/* Product & Price */}
          <div className="mb-3 border-t border-gray-100 pt-3">
            <div className="text-sm text-gray-900 font-medium mb-1">{order.offername || "N/A"}</div>
            <div className="flex items-center justify-between">
              <div className="text-xs text-gray-500">{t('orders.quantity')}: {order.count || 1}</div>
              <div className="text-sm font-semibold text-gray-900">
                {order.price || 0} {order.currency || "MKD"}
              </div>
            </div>
          </div>

          {/* Account & Source */}
          <div className="mb-3 space-y-2">
            <div className="flex items-center text-xs">
              <UserCheck className="w-3 h-3 text-gray-400 mr-2" />
              <span className="text-gray-600">{t('orders.accountLabel')}:</span>
              <span className="ml-2 font-medium text-gray-900 truncate">
                #{order.user || "N/A"} {order.accountInfo?.username ? `(${order.accountInfo.username})` : ""}
              </span>
            </div>
            <div className="flex items-center text-xs">
              <Globe className="w-3 h-3 text-gray-400 mr-2" />
              <span className="text-gray-600">{t('orders.sourceLabel')}:</span>
              <span className="ml-2 font-medium text-gray-900 truncate">
                #{order.wm || "N/A"} {order.sourceInfo?.name ? `(${order.sourceInfo.name})` : ""}
              </span>
            </div>
          </div>

          {/* Tracking */}
          {order.trackingId && (
            <div className="mb-3 flex items-center">
              <Truck className="w-4 h-4 text-gray-400 mr-2" />
              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800 truncate">
                {order.trackingId}
              </span>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-2 pt-3 border-t border-gray-100">
            <button className="text-[#264a3e] hover:text-[#1e3a30] p-2 rounded-md hover:bg-gray-50 transition-colors touch-manipulation">
              <Eye className="w-4 h-4" />
            </button>
            <button className="text-gray-400 hover:text-gray-600 p-2 rounded-md hover:bg-gray-50 transition-colors touch-manipulation">
              <Edit className="w-4 h-4" />
            </button>
          </div>
        </div>
        );
        })}
      </div>
    );
  };

  const renderOrdersTable = () => (
    <div
      className="bg-white shadow overflow-hidden sm:rounded-lg w-full max-w-full"
      style={{ maxWidth }}
    >
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('orders.orderId')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[400px]">
                {t('orders.customer')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('common.status')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('common.country')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('orders.account')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('orders.source')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('common.product')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('common.price')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('common.phone')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('common.date')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('orders.tracking')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('common.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {(orders || []).map((order: Order) => (
              <tr key={order.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  #{order.id}
                </td>
                <td className="px-6 py-4 max-w-[300px]">
                  <div className="text-sm font-medium text-gray-900 break-words">
                    {order.name || t('orders.notAvailable')}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      order.statusInfo?.color || "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {order.statusInfo?.name || `Status ${order.status}`}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    <span className="mr-1">
                      {getCountryFlag(order.country || "")}
                    </span>
                    {(order.country || "").toUpperCase() || t('orders.notAvailable')}
                  </span>
                  <div className="text-xs text-gray-500 mt-1">
                    {order.city || t('orders.notAvailable')}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {(() => {
                    // For manual orders without user, show creator
                    if ((!order.user || order.user === null) && order.source === "manual" && order.createdBy) {
                      return (
                        <div className="flex items-center">
                          <UserCheck className="w-4 h-4 text-gray-400 mr-2" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {order.createdBy.username}
                            </div>
                            <div className="text-xs text-gray-500">{order.createdBy.role}</div>
                          </div>
                        </div>
                      );
                    }
                    
                    return (
                      <div className="flex items-center">
                        <UserCheck className="w-4 h-4 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            #{order.user || t('orders.notAvailable')}
                          </div>
                          <div className="text-xs text-gray-500">
                            {order.accountInfo?.username || t('orders.notAvailable')}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {(() => {
                    // For manual orders without source ID, show "Outbound"
                    if ((!order.wm || order.wm === null) && order.source === "manual") {
                      return (
                        <div className="flex items-center">
                          <Globe className="w-4 h-4 text-gray-400 mr-2" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {t('orders.outbound')}
                            </div>
                            <div className="text-xs text-gray-500">{t('orders.manualEntry')}</div>
                          </div>
                        </div>
                      );
                    }
                    
                    return (
                      <div className="flex items-center">
                        <Globe className="w-4 h-4 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            #{order.wm || t('orders.notAvailable')}
                          </div>
                          <div className="text-xs text-gray-500">
                            {order.sourceInfo?.name || t('orders.notAvailable')}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {order.offername || t('orders.notAvailable')}
                  </div>
                  <div className="text-sm text-gray-500">
                    {t('orders.quantity')}: {order.count || 1}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {order.price || 0} {order.currency || "MKD"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <Phone className="w-4 h-4 text-gray-400 mr-2" />
                    <div className="text-sm text-gray-900">
                      {order.phone || t('orders.notAvailable')}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                    {formatTimestampFull(order.time)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {order.trackingId ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <Truck className="w-3 h-3 mr-1" />
                      {order.trackingId}
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button className="text-[#264a3e] hover:text-[#1e3a30] mr-3">
                    <Eye className="w-4 h-4" />
                  </button>
                  <button className="text-gray-400 hover:text-gray-600">
                    <Edit className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="bg-gray-50">
        <PageHeader
          icon={ShoppingCart}
          title={t('orders.monitorTitle')}
          description={t('orders.description')}
          tabs={[]}
          activeTab=""
          onTabChange={() => {}}
          customButtons={[
            {
              id: "time-period",
              label: t('dashboard.timePeriod'),
              type: "select",
              options: [
                { value: "today", label: t('time.today') },
                { value: "this-week", label: t('time.thisWeek') },
                { value: "this-month", label: t('time.thisMonth') },
                { value: "this-year", label: t('time.thisYear') },
                { value: "all-time", label: t('dashboard.allTime') },
                { value: "custom", label: t('time.custom') },
              ],
              value: timePeriod,
              onChange: (value: string) => {
                setLoading(true);
                setTimePeriod(value);
                setShowCustomDateRange(value === "custom");
              },
            },
            {
              id: "export",
              label: t('orders.exportCSV'),
              icon: Download,
              onClick: handleExportCSV,
              variant: "secondary",
              disabled: true,
            },
            {
              id: "refresh",
              label: t('dashboard.refresh'),
              icon: RefreshCw,
              onClick: () => {
                setCurrentPage(1);
                handleRetry();
              },
              variant: "primary",
              disabled: loading,
            },
          ]}
        />
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-50">
        <PageHeader
          icon={ShoppingCart}
          title={t('orders.monitorTitle')}
          description={t('orders.description')}
          tabs={[]}
          activeTab=""
          onTabChange={() => {}}
          customButtons={[
            {
              id: "time-period",
              label: t('dashboard.timePeriod'),
              type: "select",
              options: [
                { value: "today", label: t('time.today') },
                { value: "this-week", label: t('time.thisWeek') },
                { value: "this-month", label: t('time.thisMonth') },
                { value: "this-year", label: t('time.thisYear') },
                { value: "all-time", label: t('dashboard.allTime') },
                { value: "custom", label: t('time.custom') },
              ],
              value: timePeriod,
              onChange: (value: string) => {
                setLoading(true);
                setTimePeriod(value);
                setShowCustomDateRange(value === "custom");
              },
            },
            {
              id: "export",
              label: t('orders.exportCSV'),
              icon: Download,
              onClick: handleExportCSV,
              variant: "secondary",
              disabled: true,
            },
            {
              id: "refresh",
              label: t('dashboard.refresh'),
              icon: RefreshCw,
              onClick: () => {
                setCurrentPage(1);
                handleRetry();
              },
              variant: "primary",
              disabled: loading,
            },
          ]}
        />
        <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
          <ErrorMessage message={error} onRetry={handleRetry} />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50">
      <PageHeader
        icon={ShoppingCart}
        title={t('orders.monitorTitle')}
        description={t('orders.description')}
        tabs={[]}
        activeTab=""
        onTabChange={() => {}}
        customButtons={[
          {
            id: "time-period",
            label: t('dashboard.timePeriod'),
            type: "select",
            options: [
              { value: "today", label: t('time.today') },
              { value: "this-week", label: t('time.thisWeek') },
              { value: "this-month", label: t('time.thisMonth') },
              { value: "this-year", label: t('time.thisYear') },
              { value: "all-time", label: t('dashboard.allTime') },
              { value: "custom", label: t('time.custom') },
            ],
            value: timePeriod,
            onChange: (value: string) => {
              setLoading(true);
              setTimePeriod(value);
              setShowCustomDateRange(value === "custom");
            },
          },
          {
            id: "add-order",
            label: t('orders.addOrder'),
            icon: Plus,
            onClick: () => setIsAddOrderModalOpen(true),
            variant: "primary",
            disabled: false,
          },
          {
            id: "bulk-import",
            label: t('orders.bulkImport'),
            icon: Upload,
            onClick: () => setIsBulkImportModalOpen(true),
            variant: "secondary",
            disabled: false,
          },
          {
            id: "export",
            label: isExporting ? t('orders.exporting') : t('orders.exportCSV'),
            icon: Download,
            onClick: handleExportCSV,
            variant: "secondary",
            disabled: isExporting,
          },
          {
            id: "refresh-cache",
            label: cacheInfo?.isLoading ? t('orders.refreshingCache') : t('dashboard.refreshCache'),
            icon: Database,
            onClick: refreshCache,
            variant: "secondary",
            disabled: loading || cacheInfo?.isLoading,
          },
          {
            id: "refresh",
            label: t('dashboard.refresh'),
            icon: RefreshCw,
            onClick: () => {
              setCurrentPage(1);
              handleRetry();
            },
            variant: "primary",
            disabled: loading,
          },
        ]}
      />

      {/* Custom Date Range Selector */}
      {showCustomDateRange && (
        <div className="bg-white border-b border-gray-200">
          <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center space-x-4">
              <label className="text-sm font-medium text-gray-700">
                {t('orders.customDateRange')}
              </label>
              <input
                type="date"
                value={customDateRange.start}
                onChange={(e) =>
                  setCustomDateRange((prev) => ({
                    ...prev,
                    start: e.target.value,
                  }))
                }
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:border-[#264a3e]"
              />
              <span className="text-gray-500">{t('time.to')}</span>
              <input
                type="date"
                value={customDateRange.end}
                onChange={(e) =>
                  setCustomDateRange((prev) => ({
                    ...prev,
                    end: e.target.value,
                  }))
                }
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:border-[#264a3e]"
              />
              <button
                onClick={() => {
                  // Apply the custom date range - this triggers reload via useEffect
                  setAppliedCustomDateRange(customDateRange);
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#264a3e] hover:bg-[#1e3a32] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] transition-colors"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {t('orders.applyRange')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cache Status Info */}
      {cacheInfo && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-200">
          <div className="w-full px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  <Database className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-gray-900">
                    {t('dashboard.cacheStatus')}
                  </span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                    cacheInfo.isLoading 
                      ? "bg-yellow-100 text-yellow-800" 
                      : "bg-green-100 text-green-800"
                  }`}>
                    {cacheInfo.isLoading ? t('dashboard.refreshing') : t('dashboard.active')}
                  </span>
                </div>
                <div className="h-4 w-px bg-gray-300" />
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <span className="font-medium">{cacheInfo.totalOrders.toLocaleString()}</span>
                  <span>{t('dashboard.ordersCached')}</span>
                </div>
              </div>
              <div className="flex items-center space-x-2 text-xs text-gray-500">
                <Clock className="w-3.5 h-3.5" />
                <span>
                  {t('dashboard.lastUpdated')}{" "}
                  {cacheInfo.lastUpdate
                    ? new Date(cacheInfo.lastUpdate).toLocaleTimeString()
                    : t('dashboard.never')}
                </span>
                <span className="text-gray-400">•</span>
                <span>{t('dashboard.autoRefresh')} {cacheInfo.refreshInterval}min</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {renderStats()}
      {renderFilters()}

      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <div className="orders-container">
          {/* Show cards on mobile when viewMode is "cards", otherwise show table */}
          {viewMode === "cards" ? (
            <div className="lg:hidden">
              {renderOrdersCards()}
            </div>
          ) : null}
          {/* Always show table on desktop, or on mobile when viewMode is "table" */}
          <div className={viewMode === "cards" ? "hidden lg:block" : ""}>
            {renderOrdersTable()}
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalOrders}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            showFirstLast={true}
            maxVisiblePages={7}
          />
        </div>
      </div>

      {/* Add Order Modal */}
      <AddOrderModal
        isOpen={isAddOrderModalOpen}
        onClose={() => setIsAddOrderModalOpen(false)}
        onSave={handleAddOrder}
        accounts={[]} // AlterCPA removed for Serbia-CC
        sources={[]} // AlterCPA removed for Serbia-CC
        products={[
          // Mock product list - you can replace this with actual products from your data
          { id: 1746, name: "Prostatol Complex", price: 19.9, currency: "EUR" },
          // Add more products as needed
        ]}
        restrictedCountry={isCountryRestricted ? countryFilter : undefined}
        isCountryRestricted={isCountryRestricted}
        currentUser={currentUser}
      />

      {/* Bulk Import Modal */}
      <BulkImportModal
        isOpen={isBulkImportModalOpen}
        onClose={() => setIsBulkImportModalOpen(false)}
        onSave={handleBulkImport}
        accounts={[]} // AlterCPA removed for Serbia-CC
        sources={[]} // AlterCPA removed for Serbia-CC
        products={[
          // Mock product list - you can replace this with actual products from your data
          { id: 1746, name: "Prostatol Complex", price: 19.9, currency: "EUR" },
          // Add more products as needed
        ]}
        currentUser={currentUser}
      />
    </div>
  );
};

export default OrdersMain;
